// client/src/components/secure/training/Videos.js — Training → Library → Videos: every video
// file kept on the server (uploads, the per-course summary videos, the per-role app tours).
// Supervisors upload (with progress), preview, rename the title, replace the file or poster;
// the managing broker deletes (a video lessons still play is detached from them first, on
// a second confirmation). VideoPicker is the same list as a chooser for the lesson editor.
import React, { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../../../hooks/useApiClient';
import { Btn, Field, Modal, Notice, ProgressBar, fmtBytes, fmtDateTime, inputCls, uploadWithProgress } from './shared';

const ACCEPT = '.mp4,.webm,.mov,video/mp4,video/webm,video/quicktime';
const POSTER_ACCEPT = '.png,.jpg,.jpeg,.webp';

/** Upload one video (and an optional poster); calls onDone(answer). */
export const VideoUpload = ({ onDone, maxMb = 500, lessonId = null, compact = false }) => {
    const [file, setFile] = useState(null);
    const [poster, setPoster] = useState(null);
    const [title, setTitle] = useState('');
    const [pct, setPct] = useState(null);
    const [error, setError] = useState('');
    const go = async () => {
        if (!file) { setError('Choose a video file first.'); return; }
        if (file.size > maxMb * 1048576) { setError(`That file is ${fmtBytes(file.size)}; the limit is ${maxMb} MB. Shorten or compress it first.`); return; }
        const fd = new FormData();
        if (title.trim()) fd.append('title', title.trim());
        fd.append('file', file);
        if (poster) fd.append('poster', poster);
        setError(''); setPct(0);
        try {
            const r = await uploadWithProgress(lessonId ? `/api/training/lessons/${lessonId}/video` : '/api/training/media/videos', fd, setPct);
            setFile(null); setPoster(null); setTitle('');
            onDone && onDone(r);
        } catch (e) { setError(e.message); }
        finally { setPct(null); }
    };
    return (
        <div className={compact ? '' : 'border rounded p-3 bg-slate-50'}>
            {error && <Notice tone="bad">{error}</Notice>}
            <div className="grid md:grid-cols-3 gap-x-3 items-end">
                <Field label="Video file" hint={`.mp4 (best), .webm or .mov, up to ${maxMb} MB`}><input type="file" accept={ACCEPT} className="text-sm" onChange={(e) => setFile(e.target.files[0] || null)} /></Field>
                <Field label="Title (optional)"><input className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} placeholder={file ? file.name.replace(/\.[^.]+$/, '') : ''} /></Field>
                <Field label="Poster picture (optional)" hint="Shown before the video plays"><input type="file" accept={POSTER_ACCEPT} className="text-sm" onChange={(e) => setPoster(e.target.files[0] || null)} /></Field>
            </div>
            {pct != null ? <ProgressBar pct={pct} label={`Uploading ${file ? file.name : ''}`} /> : <Btn onClick={go} disabled={!file}>Upload video</Btn>}
        </div>
    );
};

const usedText = (v) => (v.used_by.length ? v.used_by.map((u) => `${u.course_title} → ${u.lesson_title}`).join('; ') : 'Not used by any lesson');

/** Pick a video from the library (lesson editor). onPick(video_url). */
export const VideoPicker = ({ onPick, onClose }) => {
    const api = useApiClient();
    const [d, setD] = useState(null);
    const [error, setError] = useState('');
    const [q, setQ] = useState('');
    useEffect(() => { api.get('/api/training/media/videos').then(setD).catch((e) => setError(e.message)); }, [api]);
    const rows = d ? d.videos.filter((v) => !v.file_missing && `${v.title || ''} ${v.media_path}`.toLowerCase().includes(q.toLowerCase())) : [];
    return (
        <Modal title="Choose a video from the library" onClose={onClose} wide>
            {error && <Notice tone="bad">{error}</Notice>}
            {!d ? <p>Loading…</p> : (
                <>
                    <input className={`${inputCls} mb-3`} placeholder="Search by title or file name" value={q} onChange={(e) => setQ(e.target.value)} />
                    <ul className="divide-y max-h-96 overflow-y-auto text-sm">
                        {rows.map((v) => (
                            <li key={v.media_path} className="py-2 flex justify-between gap-2">
                                <span><strong>{v.title || v.media_path.split('/').pop()}</strong> <span className="text-slate-500">{v.media_path} · {fmtBytes(v.size)}</span><div className="text-xs text-slate-500">{usedText(v)}</div></span>
                                <Btn kind="ghost" onClick={() => onPick(v.video_url)}>Use this</Btn>
                            </li>
                        ))}
                        {!rows.length && <li className="py-2 text-slate-600">No videos yet. Upload one under Training → Library → Videos, or with "Upload video" in the lesson.</li>}
                    </ul>
                </>
            )}
        </Modal>
    );
};

const Videos = () => {
    const api = useApiClient();
    const [d, setD] = useState(null);
    const [error, setError] = useState('');
    const [msg, setMsg] = useState('');
    const [q, setQ] = useState('');
    const [folder, setFolder] = useState('');
    const [preview, setPreview] = useState(null);
    const [rename, setRename] = useState(null);
    const [replace, setReplace] = useState(null);
    const [pct, setPct] = useState(null);

    const load = useCallback(async () => {
        try { setD(await api.get('/api/training/media/videos')); setError(''); } catch (e) { setError(e.message); }
    }, [api]);
    useEffect(() => { load(); }, [load]);

    const saveTitle = async () => {
        try { const r = await api.put('/api/training/media/videos', { path: rename.media_path, title: rename.title }); setMsg(r.message); setRename(null); load(); } catch (e) { setError(e.message); }
    };
    const doReplace = async () => {
        if (!replace.file && !replace.poster) { setError('Choose the new video file or a poster picture.'); return; }
        const fd = new FormData();
        if (replace.file) fd.append('file', replace.file);
        if (replace.poster) fd.append('poster', replace.poster);
        setPct(0); setError('');
        try {
            const r = await uploadWithProgress(`/api/training/media/videos/replace?path=${encodeURIComponent(replace.media_path)}`, fd, setPct);
            setMsg(r.message); setReplace(null); load();
        } catch (e) { setError(e.message); }
        finally { setPct(null); }
    };
    const del = async (v) => {
        const url = `/api/training/media/videos?path=${encodeURIComponent(v.media_path)}`;
        if (!window.confirm(`Delete the video file ${v.media_path}? This cannot be undone.`)) return;
        try { const r = await api.delete(url); setMsg(r.message); load(); }
        catch (e) {
            if (e.status === 409 && e.payload && e.payload.used_by) {
                if (window.confirm(`${e.message}\n\nDetach it from those lessons and delete the file?`)) {
                    try { const r = await api.delete(`${url}&detach=1`); setMsg(r.message); load(); } catch (e2) { setError(e2.message); }
                }
            } else setError(e.message);
        }
    };

    if (!d) return error ? <Notice tone="bad">{error}</Notice> : <p>Loading…</p>;
    const folders = [...new Set(d.videos.map((v) => v.folder.split('/')[0] || '(top)'))].sort();
    const rows = d.videos.filter((v) => (!folder || (v.folder.split('/')[0] || '(top)') === folder)
        && `${v.title || ''} ${v.media_path} ${usedText(v)}`.toLowerCase().includes(q.toLowerCase()));
    return (
        <div className="space-y-4">
            {error && <Notice tone="bad">{error}</Notice>}
            {msg && <Notice tone="ok">{msg}</Notice>}
            <section className="bg-white rounded-lg shadow p-4">
                <h3 className="font-semibold text-slate-800 mb-2">Upload a video</h3>
                <VideoUpload maxMb={d.max_mb} onDone={(r) => { setMsg(`${r.message} To use it, open a course → a video lesson → "Choose from library".`); load(); }} />
            </section>
            <section className="bg-white rounded-lg shadow p-4">
                <div className="flex flex-wrap gap-2 items-center mb-3">
                    <h3 className="font-semibold text-slate-800 mr-auto">Videos on the server ({d.videos.length})</h3>
                    <select className="border border-slate-300 rounded px-2 py-1 text-sm" value={folder} onChange={(e) => setFolder(e.target.value)}>
                        <option value="">All folders</option>{folders.map((f) => <option key={f} value={f}>{f}</option>)}
                    </select>
                    <input className="border border-slate-300 rounded px-2 py-1 text-sm" placeholder="Search" value={q} onChange={(e) => setQ(e.target.value)} />
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                        <thead><tr className="text-left text-slate-600 border-b"><th className="py-1 pr-2">Video</th><th className="pr-2">Size</th><th className="pr-2">Date</th><th className="pr-2">Used by</th><th /></tr></thead>
                        <tbody>
                            {rows.map((v) => (
                                <tr key={v.media_path} className="border-b align-top">
                                    <td className="py-2 pr-2"><strong>{v.title || v.media_path.split('/').pop()}</strong><div className="text-xs text-slate-500 break-all">{v.media_path}</div>
                                        {v.file_missing && <div className="text-xs text-red-700">File missing on the server — replace it or change the lessons.</div>}</td>
                                    <td className="pr-2 whitespace-nowrap">{fmtBytes(v.size)}</td>
                                    <td className="pr-2 whitespace-nowrap">{v.modified_at ? fmtDateTime(v.modified_at) : '—'}</td>
                                    <td className="pr-2 text-xs max-w-xs">{usedText(v)}</td>
                                    <td className="whitespace-nowrap space-x-2">
                                        {v.preview && <button type="button" className="text-indigo-700 underline" onClick={() => setPreview(v)}>Preview</button>}
                                        {!v.file_missing && <button type="button" className="text-indigo-700 underline" onClick={() => setRename({ media_path: v.media_path, title: v.title || '' })}>Rename</button>}
                                        <button type="button" className="text-indigo-700 underline" onClick={() => setReplace({ ...v, file: null, poster: null })}>Replace…</button>
                                        {d.can_delete && !v.file_missing && <button type="button" className="text-red-700 underline" onClick={() => del(v)}>Delete</button>}
                                    </td>
                                </tr>
                            ))}
                            {!rows.length && <tr><td colSpan={5} className="py-3 text-slate-600">No videos match.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </section>
            {preview && (
                <Modal title={preview.title || preview.media_path} onClose={() => setPreview(null)} wide>
                    <video src={preview.preview} poster={preview.poster || undefined} controls className="w-full rounded" />
                    <p className="text-xs text-slate-500 mt-2">{preview.media_path} · {fmtBytes(preview.size)} · {usedText(preview)}</p>
                </Modal>
            )}
            {rename && (
                <Modal title="Rename video" onClose={() => setRename(null)}>
                    <Field label="Title" hint="The name shown in this library. The file and the lessons that use it do not change."><input className={inputCls} value={rename.title} onChange={(e) => setRename({ ...rename, title: e.target.value })} /></Field>
                    <div className="flex justify-end gap-2"><Btn kind="ghost" onClick={() => setRename(null)}>Cancel</Btn><Btn onClick={saveTitle}>Save</Btn></div>
                </Modal>
            )}
            {replace && (
                <Modal title={`Replace: ${replace.title || replace.media_path}`} onClose={() => pct == null && setReplace(null)}>
                    <p className="text-sm text-slate-600 mb-3">The new file takes the old one's place, so every lesson that plays it ({replace.used_by.length}) shows the new video. It must be the same kind of file ({replace.media_path.split('.').pop()}).{replace.used_by.some((u) => u.course_status === 'approved') ? ' An approved course plays it: the course gets a new content version.' : ''}</p>
                    <Field label="New video file"><input type="file" accept={`.${replace.media_path.split('.').pop()}`} className="text-sm" onChange={(e) => setReplace({ ...replace, file: e.target.files[0] || null })} /></Field>
                    <Field label="New poster picture (optional)"><input type="file" accept={POSTER_ACCEPT} className="text-sm" onChange={(e) => setReplace({ ...replace, poster: e.target.files[0] || null })} /></Field>
                    {pct != null ? <ProgressBar pct={pct} /> : <div className="flex justify-end gap-2"><Btn kind="ghost" onClick={() => setReplace(null)}>Cancel</Btn><Btn onClick={doReplace}>Replace</Btn></div>}
                </Modal>
            )}
        </div>
    );
};

export default Videos;
